import React, { Fragment, memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Form, Formik } from 'formik'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import { FormikMultipleFileInput } from 'src/components/shared/form/formik/FormikMultipleFileInput'
import { buildStepFilesValidator } from './StepFilesForm.validator'
import type { StepFilesFormValues } from './StepFilesForm.interface'

const FORM_ID = 'partner-driver-step-files-form'

const INITIAL_VALUES: StepFilesFormValues = { files: null }

interface Props {
  isOpen: boolean
  title: string
  description: string
  submitLabel: string
  maxFiles: number
  isSubmitting: boolean
  onSubmit(files: File[]): void
  onClose(): void
}

/** Upload documents, complete BGC/MVR, LMS training and insurance declaration — files only. */
const StepFilesModalComponent = ({
  isOpen,
  title,
  description,
  submitLabel,
  maxFiles,
  isSubmitting,
  onSubmit,
  onClose,
}: Props) => {
  const validationSchema = useMemo(() => {
    return buildStepFilesValidator(maxFiles)
  }, [maxFiles])

  const handleSubmit = useCallback(
    (values: StepFilesFormValues) => {
      onSubmit(values.files ?? [])
    },
    [onSubmit],
  )

  return (
    <Formik<StepFilesFormValues>
      initialValues={INITIAL_VALUES}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values }) => {
        // Every caller needs at least one file, so Submit waits for one.
        const hasFiles = (values.files?.length ?? 0) > 0

        return (
          <CommonModal
            isOpen={isOpen}
            title={title}
            className={tw`max-w-[560px]`}
            onClose={onClose}
            buttons={
              <Fragment>
                <Button size="small" color="grey" disabled={isSubmitting} onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  color="dark"
                  type="submit"
                  form={FORM_ID}
                  loading={isSubmitting}
                  disabled={isSubmitting || !hasFiles}
                  data-testid="partner-driver-step-files-submit-button"
                >
                  {submitLabel}
                </Button>
              </Fragment>
            }
          >
            <Form id={FORM_ID} css={tw`flex flex-col gap-4`}>
              <Typography variant="body2">{description}</Typography>
              <FormikMultipleFileInput<StepFilesFormValues>
                name="files"
                label={maxFiles === 1 ? 'File' : `Files (up to ${maxFiles})`}
                inputDataTestId="partner-driver-step-files-input"
              />
            </Form>
          </CommonModal>
        )
      }}
    </Formik>
  )
}

export const StepFilesModal = memo(StepFilesModalComponent)
